import { useState, useRef } from 'react'
import { Plus, X, Trash2, Check, ChevronUp, ChevronDown, ArrowUp, ArrowDown, Target as TargetIcon, Shield as ShieldIcon, Link2, Edit2 } from 'lucide-react'
import { TIDEditSection, blankTID } from './TIDSection'

// Phase 3 of the FRAGO Rework — the structured three-step builder.
//
//   STEP 1  Activation Trigger ("What starts it?")
//   STEP 2  Phases & Objectives ("What are the objectives?")
//   STEP 3  Resolution ("How does it end?")
//
// Renders all three steps stacked (not a wizard) so the GM can see the
// whole FRAGO at once. Submits the assembled tree to onSubmit; the
// parent runs the writes against bounties + frago_phases +
// frago_objectives + frago_trigger_qr atomically.

const C = {
  display: { fontFamily: "'VT323', 'Share Tech Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)', position: 'relative' },
  panelBlue: { background: 'rgba(77,160,255,.04)', border: '1px solid rgba(77,160,255,.25)' },
  panelAmber: { background: 'rgba(255,204,51,.04)', border: '1px solid rgba(255,204,51,.25)' },
  panelGreen: { background: 'rgba(154,255,154,.04)', border: '1px solid rgba(154,255,154,.25)' },
  input: { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '8px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", outline: 'none' },
  select: { width: '100%', background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 },
  btnPrimary: { fontFamily: "'VT323', monospace", fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#ffcc33', color: '#000', border: 'none', padding: '6px 14px', cursor: 'pointer', boxShadow: '0 0 6px #ffcc33', display: 'inline-flex', alignItems: 'center', gap: 6 },
  btnSecondary: { fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(0, 20, 8, .4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '6px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnGhost: { background: 'none', border: '1px dashed rgba(0,255,102,.25)', color: '#9aff9a', padding: '4px 10px', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  stepHeader: { fontFamily: "'VT323', monospace", fontSize: 16, color: '#ffcc33', letterSpacing: '0.1em', textTransform: 'uppercase', textShadow: '0 0 4px rgba(255,204,51,.4)' },
  label: { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#9aff9a', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 },
}

// Activation trigger catalog. Each entry knows its config shape and which
// entity pickers it needs. Phase 4 uses the same TYPES list when matching
// scan events to queued FRAGOs.
const TRIGGER_TYPES = [
  { value: 'manual',          label: 'Manual — GM activates' },
  { value: 'scheduled',       label: 'Scheduled — auto-fire at time' },
  { value: 'qr_discovery',    label: 'QR Discovery — field code' },
  { value: 'player_scan',     label: 'Player Scan — on scan of player' },
  { value: 'equipment_scan',  label: 'Equipment Scan — on scan of item' },
  { value: 'checkpoint_scan', label: 'Checkpoint Scan — on capture' },
  { value: 'kill_event',      label: 'Kill Event — on player killed' },
  { value: 'frago_chain',     label: 'FRAGO Chain — on parent FRAGO outcome' },
  { value: 'event_phase',     label: 'Event Phase — on phase change' },
]

const OBJECTIVE_TYPES = [
  { value: 'equipment_scan',  label: 'Equipment Scan', needs: 'equipment' },
  { value: 'checkpoint_scan', label: 'Checkpoint Scan', needs: 'checkpoint' },
  { value: 'player_scan',     label: 'Player Scan (contact)', needs: 'player' },
  { value: 'kill_target',     label: 'Kill Target', needs: 'player' },
  { value: 'timed_hold',      label: 'Timed Hold', needs: null, duration: true },
  { value: 'defend_player',   label: 'Defend Player', needs: 'player', duration: true },
  { value: 'escort_player',   label: 'Escort Player', needs: 'player', duration: true, destination: true },
  { value: 'manual',          label: 'Manual (GM marks complete)', needs: null },
]

const blankObjective = () => ({
  title: '',
  type: 'equipment_scan',
  target_id: '',
  duration_minutes: '',
  destination_id: '',
  concurrent: false,
  // FRAGO restructure — optional notify-on-complete + opposing-team
  // effect. The toggle drives form visibility; the value writes
  // through to frago_objectives.notify_config / opposing_effect on
  // save (null when toggled off).
  notify_enabled: false,
  notify_who: 'commander',
  notify_message: '',
  opposing_enabled: false,
  opposing_message: '',
  opposing_trigger_frago_id: '',
  // FRAGO feature — activate a queued bounty on the opposing side
  // when this objective lands. Pairs with trigger_frago_id (counter-
  // FRAGO chain) so the GM can react to either a FRAGO or a bounty
  // outcome on completion.
  opposing_activate_bounty_id: '',
  // ANU-168 — fail condition. When true, a qualifying scan of this
  // objective's target auto-fails the parent FRAGO instead of completing it.
  is_fail_condition: false,
  fail_faction_scope: 'opposing',
})
const blankPhase = () => ({
  title: '',
  briefing: '',
  assignmentType: 'broadcast',
  assignmentNodeId: '',
  // FRAGO restructure — per-phase point award. Enables partial
  // credit (Phase 1 done, Phase 2 fail still earns Phase 1 pts).
  phaseScore: '',
  objectives: [blankObjective()],
})

export default function FragoBuilder({
  players = [],
  factions = [],
  mcsNodes = [],
  equipment = [],
  checkpoints = [],
  queuedBounties = [],
  queuedFragos = [],
  submitting,
  onSubmit,
  onCancel,
  eventId = null,
  initialValues = null,
  isEditMode = false,
}) {
  // ── FRAGO-level metadata ─────────────────────────────────────────
  const [title, setTitle] = useState(initialValues?.title ?? '')
  // FRAGO restructure — top-level narrative. Shown to the player in
  // MISSIONS / dossier as the mission statement; written to
  // bounties.frago_description. Distinct from notes (GM-side) and
  // mission_name (headline).
  const [description, setDescription] = useState(initialValues?.description ?? '')
  const tidUploadFolder = useRef(`${eventId || 'tmp'}/t_id/${crypto.randomUUID()}`).current

  // UX cleanup — top-level `points` is gone. Point values live in
  // per-phase phase_score and (optionally) bounties.end_score_override.
  const [overallTimeout, setOverallTimeout] = useState(initialValues?.overallTimeout ?? '')
  // FRAGO restructure — GM-pinned total score. Empty = auto-tally
  // from frago_phases.phase_score. Non-empty overrides the sum.
  const [endScoreOverride, setEndScoreOverride] = useState(initialValues?.endScoreOverride ?? '')

  // ── STEP 1 — Activation Trigger ──────────────────────────────────
  const [triggerType, setTriggerType] = useState(initialValues?.triggerType ?? 'manual')
  const [triggerConfig, setTriggerConfig] = useState(initialValues?.triggerConfig ?? {})
  const [activationMessage, setActivationMessage] = useState(initialValues?.activationMessage ?? '')
  const [commanderNotify, setCommanderNotify] = useState(initialValues?.commanderNotify ?? true)
  // ANU-4: separate message body for the faction commander push. Null
  // falls back to activation_message in fragoEngine.activateFrago.
  const [commanderMessage, setCommanderMessage] = useState(initialValues?.commanderMessage ?? '')
  // ANU-41 — top-level broadcast audience. Empty string means "all
  // factions" (true broadcast).
  const [targetFactionId, setTargetFactionId] = useState(initialValues?.targetFactionId ?? '')
  // FRAGO restructure — opposing-team alert at trigger time. When the
  // FRAGO activates, push {message} to every event_players row in a
  // faction OTHER than target_faction_id. Default off; enabling
  // surfaces the message textarea.
  const [opposingNotifyEnabled, setOpposingNotifyEnabled] = useState(initialValues?.opposingNotifyEnabled ?? false)
  const [opposingNotifyMessage, setOpposingNotifyMessage] = useState(initialValues?.opposingNotifyMessage ?? '')
  // Routing is auto-derived server-side from activation_trigger_type:
  //   GM-initiated (manual / scheduled / event_phase / frago_chain)
  //     → target faction's root MCS commander
  //   Field-triggered (scans / kills / qr_discovery)
  //     → chain-of-command from scanner via _resolve_chain_recipient
  // No GM-facing dropdown; the picker confused operators in the
  // field. commander_route + commander_route_node_id columns stay
  // on the schema for the legacy renderer but the builder doesn't
  // write them.
  // SEC 4 — scenario role assignments. Each row: {user_id,
  // role_name, description, visible_to: faction_id|'all'|'actor_only'}.
  // The visible_to scoping is a hint for downstream UX (the role
  // banner, scoped bounty/FRAGO visibility on the actor's faction
  // page, etc.); the server stores the array verbatim.
  const [scenarioRoles, setScenarioRoles] = useState(initialValues?.scenarioRoles ?? [])
  const addScenarioRole = () => setScenarioRoles(r => [...r, { user_id: '', role_name: '', description: '', visible_to: 'all' }])
  const updateScenarioRole = (idx, patch) => setScenarioRoles(r => r.map((x, i) => i === idx ? { ...x, ...patch } : x))
  const removeScenarioRole = (idx) => setScenarioRoles(r => r.filter((_, i) => i !== idx))

  // Helper: update a single config key without losing the rest.
  const setTriggerConfigKey = (k, v) => setTriggerConfig(prev => ({ ...prev, [k]: v }))

  // ── STEP 2 — Phases ──────────────────────────────────────────────
  const [phases, setPhases] = useState(initialValues?.phases ?? [blankPhase()])

  // ── ANU-82 — Inline Linked Bounties ───────────────────────────────
  // Saved bounty cards + an in-progress draft form (one at a time).
  // Each saved row stores phase/objective references as INDICES, not
  // UUIDs — the parent's submit handler resolves them after phases +
  // objectives are inserted (their UUIDs don't exist until then).
  const [linkedBounties, setLinkedBounties] = useState([])
  const [lbDraft, setLbDraft] = useState(null) // null = form closed
  const [lbEditIdx, setLbEditIdx] = useState(null)

  const blankLinkedDraft = () => ({
    type: 'kill',
    target_id: '',
    title: '',
    points: 100,
    active_duration: 0,
    active_duration_unit: 'minutes',
    activation_mode: 'with_frago',
    activation_phase_idx: '',
    activation_objective_phase_idx: '',
    activation_objective_idx: '',
    cancel_mode: 'never',
    cancel_objective_phase_idx: '',
    cancel_objective_idx: '',
    phase_scoped: true,
    persist_post_frago: false,
    tid: blankTID(),
  })

  const openLinkedDraftForNew = () => { setLbDraft(blankLinkedDraft()); setLbEditIdx(null); setLbTargetSearch('') }
  const openLinkedDraftForEdit = (idx) => { setLbDraft({ ...linkedBounties[idx] }); setLbEditIdx(idx); setLbTargetSearch('') }
  const [lbTargetSearch, setLbTargetSearch] = useState('')
  const cancelLinkedDraft = () => { setLbDraft(null); setLbEditIdx(null); setLbTargetSearch('') }
  const openLinkedDraftForPhase = (phaseIdx) => { setLbDraft({ ...blankLinkedDraft(), activation_mode: 'on_phase', activation_phase_idx: phaseIdx }); setLbEditIdx(null); setLbTargetSearch('') }
  const saveLinkedDraft = () => {
    if (!lbDraft) return
    if (!lbDraft.target_id) { alert('Pick a target player for the linked bounty.'); return }
    if (!lbDraft.points || parseInt(lbDraft.points, 10) <= 0) { alert('Linked bounty needs a points value.'); return }

    if (lbDraft.activation_mode === 'on_phase' && lbDraft.activation_phase_idx === '') { alert('Pick which phase activates this bounty.'); return }
    if (lbDraft.activation_mode === 'on_objective' && (lbDraft.activation_objective_phase_idx === '' || lbDraft.activation_objective_idx === '')) { alert('Pick which objective activates this bounty.'); return }
    if (lbDraft.cancel_mode === 'on_objective' && (lbDraft.cancel_objective_phase_idx === '' || lbDraft.cancel_objective_idx === '')) { alert('Pick which objective cancels this bounty.'); return }
    setLinkedBounties(prev => {
      if (lbEditIdx == null) return [...prev, lbDraft]
      return prev.map((b, i) => i === lbEditIdx ? lbDraft : b)
    })
    setLbDraft(null)
    setLbEditIdx(null)
    setLbTargetSearch('')
  }
  const removeLinkedBounty = (idx) => setLinkedBounties(prev => prev.filter((_, i) => i !== idx))
  const setLbField = (k, v) => setLbDraft(d => ({ ...d, [k]: v }))

  // ANU-122: shared render function so the lbDraft form can appear inline
  // within a phase card (when opened via ADD BOUNTY (phase)) or in the global
  // LINKED BOUNTIES section (when opened via the top-level ADD BOUNTY button).
  const renderLbDraftForm = () => {
    if (!lbDraft) return null
    const allTargets = players
    return (
      <div style={{ background: 'rgba(0,0,0,.25)', border: '1px dashed rgba(255,140,40,.4)', padding: 12, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Type */}
        <div>
          <label style={{ ...C.label, color: '#ff9a3d' }}>BOUNTY TYPE</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {[
              { key: 'kill', label: 'KILL', enabled: true },
              { key: 'escort', label: 'ESCORT (post-Sept)', enabled: false },
              { key: 'defend', label: 'DEFEND', enabled: true },
            ].map(t => (
              <button key={t.key} type="button"
                onClick={() => t.enabled && setLbField('type', t.key)}
                disabled={!t.enabled}
                style={{
                  padding: '8px',
                  fontFamily: "'VT323', monospace",
                  fontSize: 13,
                  background: lbDraft.type === t.key ? '#ff9a3d' : 'rgba(0,20,8,.4)',
                  color: lbDraft.type === t.key ? '#000' : (t.enabled ? '#ff9a3d' : '#5a4a3a'),
                  border: lbDraft.type === t.key ? 'none' : '1px solid rgba(255,140,40,.25)',
                  cursor: t.enabled ? 'pointer' : 'not-allowed',
                  opacity: t.enabled ? 1 : 0.5,
                  letterSpacing: '0.1em',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Target */}
        <div>
          <label style={{ ...C.label, color: '#ff9a3d' }}>TARGET PLAYER</label>
          <input
            type="text"
            value={lbTargetSearch}
            onChange={e => setLbTargetSearch(e.target.value)}
            placeholder="Search callsign or name..."
            style={{ ...C.input, marginBottom: 4 }}
          />
          <select value={lbDraft.target_id} onChange={e => { setLbField('target_id', e.target.value); if (!e.target.value) setLbField('tid', blankTID()) }} style={C.select}>
            <option value="">— Select target —</option>
            {allTargets
              .filter(p => {
                if (!lbTargetSearch.trim()) return true
                const q = lbTargetSearch.trim().toLowerCase()
                return (p.users?.callsign || '').toLowerCase().includes(q)
                  || (p.users?.display_name || '').toLowerCase().includes(q)
              })
              .sort((a, b) => {
                const fa = (factions.find(f => f.id === a.faction_id)?.name || '~')
                const fb = (factions.find(f => f.id === b.faction_id)?.name || '~')
                if (fa !== fb) return fa.localeCompare(fb)
                return (a.users?.callsign || a.users?.display_name || '').localeCompare(b.users?.callsign || b.users?.display_name || '')
              })
              .map(p => {
                const fac = factions.find(f => f.id === p.faction_id)
                const tag = fac ? `[${(fac.short_name || fac.name || '').toUpperCase().slice(0, 4)}] ` : ''
                return (
                  <option key={p.user_id} value={p.user_id} style={{ background: '#0a140a' }}>
                    {tag}{p.users?.callsign || p.users?.display_name || '—'}
                  </option>
                )
              })}
          </select>
          <p style={{ ...C.mono, fontSize: 9, color: '#8a6a4a', marginTop: 2 }}>All event players shown. Faction tag in brackets. Sorted by faction then callsign.</p>
        </div>

        {/* ANU-171 — T-ID inline below target picker */}
        {lbDraft.target_id && (
          <TIDEditSection
            tid={lbDraft.tid || blankTID()}
            onChange={v => setLbField('tid', v)}
            eventId={eventId}
            uploadFolder={tidUploadFolder}
            disabled={false}
          />
        )}

        {/* Title / Points */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ ...C.label, color: '#ff9a3d' }}>TITLE (optional)</label>
            <input type="text" value={lbDraft.title} onChange={e => setLbField('title', e.target.value)} placeholder="e.g. Kill the Pilot" maxLength={80} style={C.input} />
          </div>
          <div>
            <label style={{ ...C.label, color: '#ff9a3d' }}>POINTS</label>
            <input type="number" value={lbDraft.points} onChange={e => setLbField('points', e.target.value)} min={0} max={10000} style={C.input} />
          </div>
        </div>

        {lbDraft.cancel_mode === 'never' ? (
          <div>
            <label style={{ ...C.label, color: '#ff9a3d' }}>ACTIVE DURATION</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="number" value={lbDraft.active_duration} onChange={e => setLbField('active_duration', e.target.value)} min={0} max={1440} style={{ ...C.input, flex: 1 }} />
              <select value={lbDraft.active_duration_unit} onChange={e => setLbField('active_duration_unit', e.target.value)} style={{ ...C.select, width: 120 }}>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
            </div>
          </div>
        ) : (
          <p style={{ ...C.mono, fontSize: 10, color: '#8a6a4a', padding: '6px 0' }}>
            Duration overridden by cancel trigger
          </p>
        )}

        {/* Activation Trigger */}
        <div>
          <label style={{ ...C.label, color: '#ff9a3d' }}>ACTIVATION TRIGGER</label>
          <select value={lbDraft.activation_mode} onChange={e => setLbField('activation_mode', e.target.value)} style={C.select}>
            <option value="with_frago">WITH FRAGO — activate when FRAGO activates</option>
            <option value="on_objective">ON OBJECTIVE COMPLETE</option>
            <option value="on_phase">ON PHASE COMPLETE</option>
          </select>
          {lbDraft.activation_mode === 'on_phase' && (
            <select value={lbDraft.activation_phase_idx} onChange={e => setLbField('activation_phase_idx', e.target.value)} style={{ ...C.select, marginTop: 6 }}>
              <option value="">— Select phase —</option>
              {phases.map((ph, idx) => (
                <option key={idx} value={idx} style={{ background: '#0a140a' }}>Phase {idx + 1}: {ph.title || '(untitled)'}</option>
              ))}
            </select>
          )}
          {lbDraft.activation_mode === 'on_objective' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
              <select value={lbDraft.activation_objective_phase_idx} onChange={e => setLbField('activation_objective_phase_idx', e.target.value)} style={C.select}>
                <option value="">— Phase —</option>
                {phases.map((ph, idx) => (
                  <option key={idx} value={idx} style={{ background: '#0a140a' }}>P{idx + 1}: {ph.title || '(untitled)'}</option>
                ))}
              </select>
              <select
                value={lbDraft.activation_objective_idx}
                onChange={e => setLbField('activation_objective_idx', e.target.value)}
                style={C.select}
                disabled={lbDraft.activation_objective_phase_idx === ''}
              >
                <option value="">— Objective —</option>
                {lbDraft.activation_objective_phase_idx !== '' &&
                  (phases[parseInt(lbDraft.activation_objective_phase_idx, 10)]?.objectives || []).map((o, idx) => (
                    <option key={idx} value={idx} style={{ background: '#0a140a' }}>O{idx + 1}: {o.title || '(untitled)'}</option>
                  ))}
              </select>
            </div>
          )}
        </div>

        {/* Cancel Trigger */}
        <div>
          <label style={{ ...C.label, color: '#ff9a3d' }}>CANCEL TRIGGER (optional)</label>
          <select value={lbDraft.cancel_mode} onChange={e => setLbField('cancel_mode', e.target.value)} style={C.select}>
            <option value="never">NEVER</option>
            <option value="on_frago_complete">ON FRAGO COMPLETE</option>
            <option value="on_frago_fail">ON FRAGO FAIL</option>
            <option value="on_objective">ON OBJECTIVE COMPLETE</option>
          </select>
          {lbDraft.cancel_mode === 'on_objective' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
              <select value={lbDraft.cancel_objective_phase_idx} onChange={e => setLbField('cancel_objective_phase_idx', e.target.value)} style={C.select}>
                <option value="">— Phase —</option>
                {phases.map((ph, idx) => (
                  <option key={idx} value={idx} style={{ background: '#0a140a' }}>P{idx + 1}: {ph.title || '(untitled)'}</option>
                ))}
              </select>
              <select
                value={lbDraft.cancel_objective_idx}
                onChange={e => setLbField('cancel_objective_idx', e.target.value)}
                style={C.select}
                disabled={lbDraft.cancel_objective_phase_idx === ''}
              >
                <option value="">— Objective —</option>
                {lbDraft.cancel_objective_phase_idx !== '' &&
                  (phases[parseInt(lbDraft.cancel_objective_phase_idx, 10)]?.objectives || []).map((o, idx) => (
                    <option key={idx} value={idx} style={{ background: '#0a140a' }}>O{idx + 1}: {o.title || '(untitled)'}</option>
                  ))}
              </select>
            </div>
          )}
        </div>

        {/* ANU-161 — Lifecycle flags */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ ...C.label, display: 'block' }}>ACTIVE FOR FULL FRAGO</span>
              <span style={{ ...C.label, color: '#8a6a4a', fontSize: 9 }}>On = bounty stays active if this phase ends early</span>
            </div>
            <div onClick={() => setLbField('phase_scoped', !lbDraft.phase_scoped)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', fontFamily:"'VT323', monospace", fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', background: !lbDraft.phase_scoped ? 'rgba(255,154,61,.15)' : 'rgba(0,20,8,.4)', color: !lbDraft.phase_scoped ? '#ff9a3d' : '#8a6a4a', border: !lbDraft.phase_scoped ? '1px solid rgba(255,140,40,.4)' : '1px solid rgba(255,140,40,.18)' }}>
              {!lbDraft.phase_scoped ? '✓ ON' : '○ OFF'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ ...C.label, display: 'block' }}>PERSIST AFTER FRAGO ENDS</span>
              <span style={{ ...C.label, color: '#8a6a4a', fontSize: 9 }}>On = bounty remains active after FRAGO concludes until ENDEX or GM closes it</span>
            </div>
            <div onClick={() => { setLbField('persist_post_frago', !lbDraft.persist_post_frago); if (!lbDraft.persist_post_frago) setLbField('phase_scoped', false) }} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', fontFamily:"'VT323', monospace", fontSize:12, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', background: lbDraft.persist_post_frago ? 'rgba(255,154,61,.15)' : 'rgba(0,20,8,.4)', color: lbDraft.persist_post_frago ? '#ff9a3d' : '#8a6a4a', border: lbDraft.persist_post_frago ? '1px solid rgba(255,140,40,.4)' : '1px solid rgba(255,140,40,.18)' }}>
              {lbDraft.persist_post_frago ? '✓ ON' : '○ OFF'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={saveLinkedDraft}
            style={{ ...C.btnPrimary, background: '#ff9a3d', boxShadow: '0 0 6px rgba(255,140,40,.5)' }}>
            <Check size={12} /> {lbEditIdx == null ? 'SAVE BOUNTY' : 'UPDATE BOUNTY'}
          </button>
          <button type="button" onClick={cancelLinkedDraft} style={C.btnSecondary}>CANCEL</button>
        </div>
      </div>
    )
  }

  const addPhase = () => setPhases(p => [...p, blankPhase()])
  const removePhase = (idx) => setPhases(p => p.filter((_, i) => i !== idx))
  const movePhase = (idx, dir) => {
    setPhases(p => {
      const next = [...p]
      const target = idx + dir
      if (target < 0 || target >= next.length) return p
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }
  const setPhaseField = (idx, k, v) => setPhases(p => p.map((ph, i) => i === idx ? { ...ph, [k]: v } : ph))
  const addObjective = (phaseIdx) => setPhases(p => p.map((ph, i) =>
    i === phaseIdx ? { ...ph, objectives: [...ph.objectives, blankObjective()] } : ph
  ))
  const removeObjective = (phaseIdx, objIdx) => setPhases(p => p.map((ph, i) =>
    i === phaseIdx ? { ...ph, objectives: ph.objectives.filter((_, j) => j !== objIdx) } : ph
  ))
  const setObjectiveField = (phaseIdx, objIdx, k, v) => setPhases(p => p.map((ph, i) =>
    i === phaseIdx
      ? { ...ph, objectives: ph.objectives.map((o, j) => j === objIdx ? { ...o, [k]: v } : o) }
      : ph
  ))

  // ── SECTION 4 — End Conditions ──────────────────────────────────
  // FRAGO restructure — timeout outcome is gone (it's just failure
  // now), and `points` was lifted out of the resolution rows into
  // per-phase phaseScore + end_score_override. resolutions only do
  // side effects (activate bounty / chain FRAGO / release message).
  const blankResolution = () => ({
    activate_bounty_id: '',
    trigger_frago_id: '',
    message: '',
    // SEC 5 — cancel / fail cascades. Stored as single-id picks in
    // the form, serialised to single-element UUID arrays on save so
    // the JSONB shape matches the directive (cancel_bounty_ids etc.)
    // and the server trigger can apply them uniformly.
    cancel_bounty_id: '',
    cancel_frago_id: '',
    fail_bounty_id: '',
    fail_frago_id: '',
  })
  const [onSuccess, setOnSuccess] = useState(initialValues?.onSuccess ?? blankResolution())
  const [onFail,    setOnFail]    = useState(initialValues?.onFail    ?? blankResolution())

  // ── Step collapse state (visual only) ────────────────────────────
  const [step1Collapsed, setStep1Collapsed] = useState(false)
  const [step2Collapsed, setStep2Collapsed] = useState(false)
  const [step3Collapsed, setStep3Collapsed] = useState(false)

  // Player picker source — flatten event_players to {id, label, faction_id}
  // sorted by callsign for predictability.
  const playerOptions = players.map(p => ({
    id: p.user_id,
    faction_id: p.faction_id,
    label: p.users?.callsign || p.users?.display_name || p.users?.email || p.user_id?.slice(0, 8),
  })).sort((a, b) => a.label.localeCompare(b.label))

  // UX cleanup — group player picker options by faction with team headers.
  // Players without a faction (unassigned roster) drop into an UNASSIGNED
  // bucket at the bottom so the GM still sees them.
  const playerGroups = (() => {
    if (!factions || factions.length === 0) {
      return [{ faction: null, label: 'PLAYERS', items: playerOptions }]
    }
    const byFaction = new Map()
    const unassigned = []
    playerOptions.forEach(p => {
      if (p.faction_id) {
        if (!byFaction.has(p.faction_id)) byFaction.set(p.faction_id, [])
        byFaction.get(p.faction_id).push(p)
      } else {
        unassigned.push(p)
      }
    })
    const groups = factions
      .filter(f => byFaction.has(f.id))
      .map(f => ({ faction: f, label: f.name?.toUpperCase() || 'FACTION', items: byFaction.get(f.id) }))
    if (unassigned.length) groups.push({ faction: null, label: 'UNASSIGNED', items: unassigned })
    return groups
  })()
  const renderPlayerOptions = () => playerGroups.map(g => (
    <optgroup key={g.faction?.id || 'unassigned'} label={g.label}>
      {g.items.map(p => (
        <option key={p.id} value={p.id} style={{ background: '#0a140a' }}>{p.label}</option>
      ))}
    </optgroup>
  ))

  const handleSubmit = () => {
    if (submitting) return
    if (!title.trim()) { alert('FRAGO title is required.'); return }

    // Validation per trigger type — surface a friendly error before the
    // round-trip rather than blowing up on a CHECK constraint.
    if (triggerType === 'scheduled' && !triggerConfig.scheduled_at) {
      alert('Scheduled trigger requires a date/time.'); return
    }
    if (triggerType === 'player_scan' && !triggerConfig.target_id) {
      alert('Pick the player whose scan should fire the FRAGO.'); return
    }
    if (triggerType === 'equipment_scan' && !triggerConfig.target_id) {
      alert('Pick the equipment whose scan should fire the FRAGO.'); return
    }
    if (triggerType === 'checkpoint_scan' && !triggerConfig.target_id) {
      alert('Pick the checkpoint whose capture should fire the FRAGO.'); return
    }
    if (triggerType === 'kill_event' && !triggerConfig.target_id) {
      alert('Pick the player whose death should fire the FRAGO.'); return
    }
    if (triggerType === 'frago_chain' && (!triggerConfig.parent_frago_id || !triggerConfig.condition)) {
      alert('Pick a parent FRAGO and a chain condition (on_success / on_fail / on_either).'); return
    }
    if (triggerType === 'event_phase' && !triggerConfig.phase) {
      alert('Pick the event phase that should activate this FRAGO.'); return
    }

    // Phase + objective validation. Empty phases are dropped silently;
    // titles are required on whatever's left, and each objective must
    // satisfy its type's CHECK constraint (target_id for entity scans,
    // duration_minutes for timed types, destination_id for escort).
    const cleanedPhases = []
    for (let i = 0; i < phases.length; i++) {
      const ph = phases[i]
      if (!ph.title.trim() && ph.objectives.every(o => !o.title.trim())) continue
      if (!ph.title.trim()) { alert(`Phase ${i + 1} needs a title.`); return }
      if (ph.assignmentType === 'delegated' && !ph.assignmentNodeId) {
        alert(`Phase "${ph.title}" is delegated — pick the MCS node.`); return
      }
      const cleanedObjs = []
      for (let j = 0; j < ph.objectives.length; j++) {
        const o = ph.objectives[j]
        if (!o.title.trim() && !o.target_id && !o.duration_minutes) continue
        const def = OBJECTIVE_TYPES.find(t => t.value === o.type)
        if (!o.title.trim()) { alert(`Phase "${ph.title}" objective ${j + 1} needs a title.`); return }
        if (def?.needs && !o.target_id) {
          alert(`Objective "${o.title}" needs a ${def.needs} target.`); return
        }
        if (def?.duration && !o.duration_minutes) {
          alert(`Objective "${o.title}" needs a duration in minutes.`); return
        }
        if (def?.destination && !o.destination_id) {
          alert(`Objective "${o.title}" needs a destination checkpoint.`); return
        }
        // FRAGO restructure — assemble the JSONB side effects for this
        // objective. Empty toggles write null so the engine knows to
        // skip without checking shape.
        const notifyConfig = o.notify_enabled && o.notify_message.trim()
          ? { who: o.notify_who, message: o.notify_message.trim() }
          : null
        const opposingEffect = o.opposing_enabled && (
          o.opposing_message.trim() || o.opposing_trigger_frago_id || o.opposing_activate_bounty_id
        )
          ? {
              message: o.opposing_message.trim() || null,
              trigger_frago_id: o.opposing_trigger_frago_id || null,
              activate_bounty_id: o.opposing_activate_bounty_id || null,
            }
          : null
        cleanedObjs.push({
          title: o.title.trim(),
          type: o.type,
          target_id: o.target_id || null,
          duration_minutes: o.duration_minutes ? parseInt(o.duration_minutes, 10) : null,
          destination_id: o.destination_id || null,
          concurrent: !!o.concurrent,
          notify_config: notifyConfig,
          opposing_effect: opposingEffect,
          is_fail_condition: !!o.is_fail_condition,
          fail_faction_scope: o.is_fail_condition ? (o.fail_faction_scope || 'opposing') : null,
        })
      }
      if (cleanedObjs.length === 0) { alert(`Phase "${ph.title}" needs at least one objective.`); return }
      cleanedPhases.push({
        title: ph.title.trim(),
        briefing: ph.briefing.trim() || null,
        assignment_type: ph.assignmentType,
        assignment_node_id: ph.assignmentType === 'delegated' ? ph.assignmentNodeId : null,
        // FRAGO restructure — points awarded when this phase completes.
        phase_score: ph.phaseScore === '' ? 0 : (parseInt(ph.phaseScore, 10) || 0),
        objectives: cleanedObjs,
      })
    }
    if (cleanedPhases.length === 0) { alert('Add at least one phase with one objective.'); return }

    // Strip empty resolution actions to keep the JSONB tidy.
    const cleanResolution = (r) => {
      const out = {}
      if (r.activate_bounty_id) out.activate_bounty_id = r.activate_bounty_id
      if (r.trigger_frago_id)   out.trigger_frago_id   = r.trigger_frago_id
      if (r.message?.trim())    out.message            = r.message.trim()
      // SEC 5 — cancel / fail cascades. Wrap single-id picks in
      // single-element arrays so the server trigger's array
      // unpacking works uniformly. Future multi-select can write
      // the array directly.
      if (r.cancel_bounty_id) out.cancel_bounty_ids = [r.cancel_bounty_id]
      if (r.cancel_frago_id)  out.cancel_frago_ids  = [r.cancel_frago_id]
      if (r.fail_bounty_id)   out.fail_bounty_ids   = [r.fail_bounty_id]
      if (r.fail_frago_id)    out.fail_frago_ids    = [r.fail_frago_id]
      return Object.keys(out).length > 0 ? out : null
    }

    // FRAGO restructure — point math.
    //   end_score_override (if set) wins. Otherwise sum of phase_score.
    //   The top-level bounties.points column mirrors the resolved total
    //   for back-compat with stats views / SITREP that read it directly.
    const phaseSum = cleanedPhases.reduce((sum, p) => sum + (p.phase_score || 0), 0)
    const overrideInt = endScoreOverride === '' ? null : (parseInt(endScoreOverride, 10) || 0)
    const resolvedTotal = overrideInt ?? phaseSum

    // ANU-82 — package the linked bounty drafts. The parent resolves
    // phase/objective indices to UUIDs after the FRAGO + phases +
    // objectives are inserted (their UUIDs don't exist until then).
    const cleanedLinkedBounties = (linkedBounties || []).map(lb => ({
      type: lb.type,
      target_id: lb.target_id,
      title: (lb.title || '').trim() || null,
      points: parseInt(lb.points, 10) || 0,
      // Normalize active_duration to minutes for the bounties.active_duration
      // column (which the ANU-80 _bounty_activate_set_timer trigger
      // reads in minutes).
      active_duration_minutes: lb.active_duration_unit === 'hours'
        ? (parseInt(lb.active_duration, 10) || 0) * 60
        : (parseInt(lb.active_duration, 10) || 0),
      activation: {
        mode: lb.activation_mode,
        phase_idx: lb.activation_mode === 'on_phase' ? parseInt(lb.activation_phase_idx, 10) : null,
        objective_phase_idx: lb.activation_mode === 'on_objective' ? parseInt(lb.activation_objective_phase_idx, 10) : null,
        objective_idx: lb.activation_mode === 'on_objective' ? parseInt(lb.activation_objective_idx, 10) : null,
      },
      cancel: {
        mode: lb.cancel_mode,
        objective_phase_idx: lb.cancel_mode === 'on_objective' ? parseInt(lb.cancel_objective_phase_idx, 10) : null,
        objective_idx: lb.cancel_mode === 'on_objective' ? parseInt(lb.cancel_objective_idx, 10) : null,
      },
      phase_scoped: lb.persist_post_frago ? false : (lb.phase_scoped ?? true),
      persist_post_frago: lb.persist_post_frago ?? false,
      tid: lb.tid?.enabled ? lb.tid : null,
    }))

    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      points: resolvedTotal,
      end_score_override: overrideInt,
      overall_timeout_minutes: overallTimeout ? parseInt(overallTimeout, 10) : null,
      activation_trigger_type: triggerType,
      activation_trigger_config: Object.keys(triggerConfig).length > 0 ? triggerConfig : null,
      activation_message: activationMessage.trim() || null,
      commander_notify: commanderNotify,
      commander_message: commanderMessage.trim() || null,
      // Routing simplified — cascade derives the recipient from
      // activation_trigger_type. Keep these fields null so legacy
      // readers don't trip on stale picker values.
      commander_route: null,
      commander_route_node_id: null,
      // SEC 4 — scenario role assignments. Drop empty rows so the
      // JSONB stays tidy; require both user_id AND role_name for a
      // row to land.
      scenario_roles: scenarioRoles
        .filter(sr => sr.user_id && sr.role_name.trim())
        .map(sr => ({
          user_id: sr.user_id,
          role_name: sr.role_name.trim(),
          description: (sr.description || '').trim() || null,
          visible_to: sr.visible_to || 'all',
        })),
      // ANU-41: empty string → null = broadcast to all factions.
      target_faction_id: targetFactionId || null,
      // FRAGO restructure — opposing-team alert at activation.
      opposing_notify_on_trigger:
        opposingNotifyEnabled && opposingNotifyMessage.trim()
          ? { message: opposingNotifyMessage.trim() }
          : null,
      resolution_on_success: cleanResolution(onSuccess),
      resolution_on_fail:    cleanResolution(onFail),
      // resolution_on_timeout intentionally dropped — timeout collapses
      // into the fail outcome per the new design.
      phases: cleanedPhases,
      // ANU-82 — child bounties spawned inline. Parent resolves the
      // index-based phase / objective references after the structural
      // inserts complete.
      linked_bounties: cleanedLinkedBounties,
    })
  }

  return (
    <div className="p-4 mb-4" style={{ ...C.panel, border: '1px solid rgba(255,204,51,.3)', background: 'rgba(255,204,51,.04)' }}>
      <div className="flex justify-between items-center mb-3">
        <p style={C.stepHeader}>{isEditMode ? '✎ EDIT FRAGO' : '◆ NEW FRAGO'}</p>
        {/* Track 6.2 — 44x44 min hit area on the builder close. */}
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', minHeight: 44, minWidth: 44, padding: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <X size={18} />
        </button>
      </div>

      {/* ───────────── SECTION 1: FRAGO INFO ───────────── */}
      <div className="p-3 mb-3" style={{ ...C.panel, border: '1px solid rgba(0,255,102,.3)' }}>
        <p style={{ ...C.stepHeader, color: '#00ff66', marginBottom: 10 }}>◆ FRAGO INFO</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: '3fr 1fr' }}>
          <div>
            <label style={C.label}>NAME *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
              placeholder="Down Pilot" style={C.input} />
          </div>
          <div>
            <label style={C.label}>OVERALL TIMEOUT (min)</label>
            <input type="number" value={overallTimeout} onChange={e => setOverallTimeout(e.target.value)} min={1} placeholder="(none)" style={C.input} />
          </div>
          {/* FRAGO restructure — narrative description shown in player
              dossier as the mission statement. Write it as a brief, not
              a database label. */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={C.label}>DESCRIPTION</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} maxLength={1000}
              placeholder="A helicopter has gone down in enemy territory. Rescue the pilot and get him to safety."
              style={{ ...C.input, resize: 'vertical', fontFamily: "'JetBrains Mono', monospace" }} />
            <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
              Shown in MISSIONS / dossier as the top-level briefing. Write in-character.
            </p>
          </div>
          {/* Routing fix — TARGET FACTION is scoping (which faction
              this FRAGO belongs to). The actual visibility entry
              point is DELIVERY METHOD below (in the Trigger panel). */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={C.label}>TARGET FACTION</label>
            <select value={targetFactionId} onChange={e => setTargetFactionId(e.target.value)} style={C.select}>
              <option value="" style={{ background: '#0a140a' }}>All factions (event-wide)</option>
              {(factions || []).map(f => (
                <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>{f.name}</option>
              ))}
            </select>
            <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
              Scoping only — DELIVERY METHOD (below) controls who actually sees it first.
            </p>
          </div>

          {/* SEC 4 — Scenario Roles. Temporary in-character role
              assignments that scope to this FRAGO. Each entry pins a
              player to a named role (e.g. "The Pilot") so the player
              sees a banner overlay while the FRAGO is live. */}
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <label style={{ ...C.label, marginBottom: 0 }}>SCENARIO ROLES ({scenarioRoles.length})</label>
              <button onClick={addScenarioRole} style={{ ...C.btnGhost, fontSize: 10 }}>
                <Plus size={10} /> ADD ACTOR
              </button>
            </div>
            {scenarioRoles.length > 0 && (
              <div className="space-y-2">
                {scenarioRoles.map((sr, idx) => (
                  <div key={idx} className="p-2" style={{ background: 'rgba(0,20,8,.3)', border: '1px solid rgba(0,255,102,.18)' }}>
                    <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
                      <div>
                        <label style={{ ...C.label, fontSize: 9 }}>PLAYER</label>
                        <select value={sr.user_id} onChange={e => updateScenarioRole(idx, { user_id: e.target.value })} style={C.select}>
                          <option value="">— pick —</option>
                          {renderPlayerOptions()}
                        </select>
                      </div>
                      <div>
                        <label style={{ ...C.label, fontSize: 9 }}>ROLE NAME</label>
                        <input value={sr.role_name} onChange={e => updateScenarioRole(idx, { role_name: e.target.value })}
                          placeholder="The Pilot" maxLength={60} style={C.input} />
                      </div>
                      <div className="flex items-end">
                        <button onClick={() => removeScenarioRole(idx)} style={{ background: 'none', border: 'none', color: '#ff2a2a', cursor: 'pointer', padding: 4 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ ...C.label, fontSize: 9 }}>DESCRIPTION</label>
                        <input value={sr.description} onChange={e => updateScenarioRole(idx, { description: e.target.value })}
                          placeholder="Downed helicopter pilot awaiting rescue" maxLength={200} style={C.input} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ ...C.label, fontSize: 9 }}>VISIBLE TO</label>
                        <select value={sr.visible_to || 'all'} onChange={e => updateScenarioRole(idx, { visible_to: e.target.value })} style={C.select}>
                          <option value="all" style={{ background: '#0a140a' }}>All factions</option>
                          <option value="actor_only" style={{ background: '#0a140a' }}>Actor only</option>
                          {(factions || []).map(f => (
                            <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>{f.name} only</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ───────────── STEP 1: Activation Trigger ───────────── */}
      <div className="p-3 mb-3" style={C.panelBlue}>
        <button onClick={() => setStep1Collapsed(s => !s)} className="flex items-center gap-2 w-full text-left"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4da0ff', padding: 0, marginBottom: step1Collapsed ? 0 : 12 }}>
          {step1Collapsed ? <ChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronUp size={14} />}
          <span style={{ ...C.stepHeader, color: '#4da0ff' }}>◆ TRIGGER — What starts this FRAGO?</span>
        </button>
        {!step1Collapsed && (<>
          <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={C.label}>TRIGGER TYPE</label>
              <select value={triggerType} onChange={e => { setTriggerType(e.target.value); setTriggerConfig({}) }} style={C.select}>
                {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value} style={{ background: '#0a140a' }}>{t.label}</option>)}
              </select>
            </div>

            {triggerType === 'scheduled' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>FIRE AT</label>
                <input type="datetime-local" value={triggerConfig.scheduled_at || ''}
                  onChange={e => setTriggerConfigKey('scheduled_at', e.target.value)} style={C.input} />
              </div>
            )}

            {triggerType === 'player_scan' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>PLAYER TO SCAN (contact, not kill)</label>
                <select value={triggerConfig.target_id || ''} onChange={e => setTriggerConfigKey('target_id', e.target.value)} style={C.select}>
                  <option value="">— pick a player —</option>
                  {renderPlayerOptions()}
                </select>
              </div>
            )}

            {triggerType === 'equipment_scan' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>EQUIPMENT THAT TRIGGERS</label>
                <select value={triggerConfig.target_id || ''} onChange={e => setTriggerConfigKey('target_id', e.target.value)} style={C.select}>
                  <option value="">— pick equipment —</option>
                  {equipment.map(eq => <option key={eq.id} value={eq.id} style={{ background: '#0a140a' }}>{eq.name}</option>)}
                </select>
              </div>
            )}

            {triggerType === 'checkpoint_scan' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>CHECKPOINT THAT TRIGGERS</label>
                <select value={triggerConfig.target_id || ''} onChange={e => setTriggerConfigKey('target_id', e.target.value)} style={C.select}>
                  <option value="">— pick a checkpoint —</option>
                  {checkpoints.map(cp => <option key={cp.id} value={cp.id} style={{ background: '#0a140a' }}>{cp.name}</option>)}
                </select>
              </div>
            )}

            {triggerType === 'kill_event' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>PLAYER WHOSE DEATH FIRES THIS</label>
                <select value={triggerConfig.target_id || ''} onChange={e => setTriggerConfigKey('target_id', e.target.value)} style={C.select}>
                  <option value="">— pick a player —</option>
                  {renderPlayerOptions()}
                </select>
              </div>
            )}

            {triggerType === 'frago_chain' && (
              <>
                <div>
                  <label style={C.label}>PARENT FRAGO</label>
                  <select value={triggerConfig.parent_frago_id || ''} onChange={e => setTriggerConfigKey('parent_frago_id', e.target.value)} style={C.select}>
                    <option value="">— pick a FRAGO —</option>
                    {queuedFragos.map(f => <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>{f.mission_name || f.id.slice(0, 8)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={C.label}>CONDITION</label>
                  <select value={triggerConfig.condition || ''} onChange={e => setTriggerConfigKey('condition', e.target.value)} style={C.select}>
                    <option value="">— pick —</option>
                    <option value="on_success" style={{ background: '#0a140a' }}>On parent success</option>
                    <option value="on_fail" style={{ background: '#0a140a' }}>On parent fail</option>
                    <option value="on_either" style={{ background: '#0a140a' }}>On either outcome</option>
                  </select>
                </div>
              </>
            )}

            {triggerType === 'event_phase' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>EVENT PHASE</label>
                <select value={triggerConfig.phase || ''} onChange={e => setTriggerConfigKey('phase', e.target.value)} style={C.select}>
                  <option value="">— pick a phase —</option>
                  <option value="prep" style={{ background: '#0a140a' }}>Prep</option>
                  <option value="registration" style={{ background: '#0a140a' }}>Registration</option>
                  <option value="locked" style={{ background: '#0a140a' }}>Locked</option>
                  <option value="live" style={{ background: '#0a140a' }}>Live</option>
                  <option value="endex" style={{ background: '#0a140a' }}>Endex</option>
                </select>
              </div>
            )}

            {/* qr_discovery has no config — code is generated on save. */}
            {triggerType === 'qr_discovery' && (
              <p style={{ ...C.mono, fontSize: 10, color: '#4da0ff', gridColumn: '1 / -1', padding: '4px 0' }}>
                A unique ANUBIS-FRAGO-xxx code is generated when you save. Print + drop the QR to activate this FRAGO.
              </p>
            )}

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={C.label}>DISCOVERY MESSAGE (optional — shown to the triggering player)</label>
              <textarea value={activationMessage} onChange={e => setActivationMessage(e.target.value)} rows={2} maxLength={500}
                placeholder="Intel suggests an enemy laptop at grid 21B..." style={{ ...C.input, resize: 'vertical', fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
              <input type="checkbox" id="commander_notify" checked={commanderNotify} onChange={e => setCommanderNotify(e.target.checked)} />
              <label htmlFor="commander_notify" style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>
                Notify the triggering player's faction commander
              </label>
            </div>
            {/* ANU-4 — commander brief is separate from the player-facing
                discovery message. Falls back to the discovery text when
                left blank so existing FRAGOs that only set one field keep
                working. */}
            {commanderNotify && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={C.label}>COMMANDER BRIEFING (optional — pushed to the faction commander)</label>
                <textarea value={commanderMessage} onChange={e => setCommanderMessage(e.target.value)} rows={2} maxLength={500}
                  placeholder="Laptop located at Building 34, Grid 4521 — dispatch a recovery team." style={{ ...C.input, resize: 'vertical', fontFamily: "'JetBrains Mono', monospace" }} />
                <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
                  If blank, the commander push falls back to the discovery message above.
                </p>
              </div>
            )}
            {/* DELIVERY METHOD picker removed. Routing now auto-
                derives from activation_trigger_type:
                  GM-initiated triggers → faction root commander
                  Field-triggered → chain-of-command from scanner
                The GM doesn't have to think about it. */}
            {commanderNotify && (
              <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', gridColumn: '1 / -1' }}>
                Routing is automatic: GM-initiated triggers go to the faction commander; scan/kill triggers walk up to the scanner's parent role.
              </p>
            )}
            {/* FRAGO restructure — opposing-team notification on trigger.
                Pushes a separate alert to factions OTHER than the
                broadcast target so the enemy knows something just
                happened ("Enemy activity detected near the crash
                site"). Reactive gameplay. */}
            <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
              <input type="checkbox" id="opposing_notify_on_trigger"
                checked={opposingNotifyEnabled} onChange={e => setOpposingNotifyEnabled(e.target.checked)} />
              <label htmlFor="opposing_notify_on_trigger" style={{ ...C.mono, fontSize: 11, color: '#ff7a3d' }}>
                Notify opposing team when this FRAGO activates
              </label>
            </div>
            {opposingNotifyEnabled && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ ...C.label, color: '#ff7a3d' }}>OPPOSING TEAM MESSAGE</label>
                <textarea value={opposingNotifyMessage} onChange={e => setOpposingNotifyMessage(e.target.value)} rows={2} maxLength={500}
                  placeholder="Enemy activity detected near the crash site." style={{ ...C.input, resize: 'vertical', borderColor: 'rgba(255,122,61,.4)', color: '#ff7a3d' }} />
                <p style={{ ...C.mono, fontSize: 10, color: '#7a3d20', marginTop: 4 }}>
                  Sent to factions other than the broadcast target. If broadcasting to ALL, sent to no one — opposing makes no sense without a faction split.
                </p>
              </div>
            )}
          </div>
        </>)}
      </div>

      {/* ───────────── STEP 2: Phases & Objectives ───────────── */}
      <div className="p-3 mb-3" style={C.panelAmber}>
        <button onClick={() => setStep2Collapsed(s => !s)} className="flex items-center gap-2 w-full text-left"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffcc33', padding: 0, marginBottom: step2Collapsed ? 0 : 12 }}>
          {step2Collapsed ? <ChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronUp size={14} />}
          <span style={{ ...C.stepHeader, color: '#ffcc33' }}>◆ PHASES — What are the objectives? ({phases.length})</span>
        </button>
        {!step2Collapsed && (<>
          {phases.map((phase, phaseIdx) => (
            <div key={phaseIdx} className="p-3 mb-3" style={{ ...C.panel, border: '1px solid rgba(255,204,51,.4)' }}>
              <div className="flex justify-between items-center mb-2">
                <p style={{ ...C.mono, fontSize: 11, color: '#ffcc33', letterSpacing: '0.1em' }}>
                  PHASE {phaseIdx + 1}
                  {phaseIdx > 0 && (
                    <span style={{ color: '#4aaa5a', marginLeft: 8 }}>· gates on Phase {phaseIdx}</span>
                  )}
                </p>
                <div className="flex gap-1">
                  <button onClick={() => movePhase(phaseIdx, -1)} disabled={phaseIdx === 0}
                    style={{ ...C.btnSecondary, fontSize: 10, opacity: phaseIdx === 0 ? 0.3 : 1 }}><ArrowUp size={10} /></button>
                  <button onClick={() => movePhase(phaseIdx, +1)} disabled={phaseIdx === phases.length - 1}
                    style={{ ...C.btnSecondary, fontSize: 10, opacity: phaseIdx === phases.length - 1 ? 0.3 : 1 }}><ArrowDown size={10} /></button>
                  {phases.length > 1 && (
                    <button onClick={() => removePhase(phaseIdx)} style={{ ...C.btnSecondary, fontSize: 10, color: '#ff2a2a', border: '1px solid rgba(255,42,42,.3)' }}>
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: '3fr 1fr' }}>
                <div>
                  <label style={C.label}>PHASE TITLE *</label>
                  <input value={phase.title} onChange={e => setPhaseField(phaseIdx, 'title', e.target.value)}
                    placeholder="Find the laptop" maxLength={80} style={C.input} />
                </div>
                {/* FRAGO restructure — per-phase points enable partial
                    credit. Final FRAGO score = sum of completed phase
                    scores (or end_score_override if set). */}
                <div>
                  <label style={C.label}>PHASE SCORE</label>
                  <input type="number" value={phase.phaseScore} onChange={e => setPhaseField(phaseIdx, 'phaseScore', e.target.value)}
                    min={0} max={10000} placeholder="0" style={C.input} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={C.label}>DESCRIPTION (dossier narrative)</label>
                  <textarea value={phase.briefing} onChange={e => setPhaseField(phaseIdx, 'briefing', e.target.value)} rows={3} maxLength={500}
                    placeholder="Gather a team and get to the pilot before the enemy can. Last known position: grid 4521."
                    style={{ ...C.input, resize: 'vertical', fontFamily: "'JetBrains Mono', monospace" }} />
                  <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
                    Shown to the player as the phase briefing when this phase unlocks. Write in-character.
                  </p>
                </div>
                <div>
                  <label style={C.label}>ASSIGNMENT</label>
                  <select value={phase.assignmentType} onChange={e => setPhaseField(phaseIdx, 'assignmentType', e.target.value)} style={C.select}>
                    <option value="broadcast" style={{ background: '#0a140a' }}>Broadcast (any faction member)</option>
                    <option value="delegated" style={{ background: '#0a140a' }}>Delegated (specific node)</option>
                    <option value="inherited" style={{ background: '#0a140a' }}>Inherited (same as previous phase)</option>
                  </select>
                </div>
                {phase.assignmentType === 'delegated' && (
                  <div>
                    <label style={C.label}>MCS NODE</label>
                    <select value={phase.assignmentNodeId} onChange={e => setPhaseField(phaseIdx, 'assignmentNodeId', e.target.value)} style={C.select}>
                      <option value="">— pick a node —</option>
                      {mcsNodes.map(n => <option key={n.id} value={n.id} style={{ background: '#0a140a' }}>{n.callsign ? `${n.callsign} — ${n.name}` : n.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Objectives */}
              <p style={{ ...C.mono, fontSize: 10, color: '#9aff9a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                OBJECTIVES ({phase.objectives.length})
              </p>
              {phase.objectives.map((obj, objIdx) => {
                const def = OBJECTIVE_TYPES.find(t => t.value === obj.type) || OBJECTIVE_TYPES[0]
                const targetOptions =
                  def.needs === 'equipment' ? equipment.map(eq => ({ id: eq.id, label: eq.name })) :
                  def.needs === 'checkpoint' ? checkpoints.map(cp => ({ id: cp.id, label: cp.name })) :
                  def.needs === 'player' ? playerOptions :
                  []
                const renderTargetOptions = () => {
                  if (def.needs === 'player') return renderPlayerOptions()
                  return targetOptions.map(o => <option key={o.id} value={o.id} style={{ background: '#0a140a' }}>{o.label}</option>)
                }
                return (
                  <div key={objIdx} className="p-2 mb-2" style={{ ...C.panel, border: '1px solid rgba(154,255,154,.2)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <p style={{ ...C.mono, fontSize: 10, color: '#9aff9a' }}>OBJECTIVE {objIdx + 1}</p>
                      {phase.objectives.length > 1 && (
                        <button onClick={() => removeObjective(phaseIdx, objIdx)}
                          style={{ background: 'none', border: 'none', color: '#ff2a2a', cursor: 'pointer' }}>
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                    <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={C.label}>TITLE *</label>
                        <input value={obj.title} onChange={e => setObjectiveField(phaseIdx, objIdx, 'title', e.target.value)}
                          placeholder="Scan enemy laptop" maxLength={80} style={C.input} />
                      </div>
                      <div>
                        <label style={C.label}>TYPE</label>
                        <select value={obj.type} onChange={e => {
                          setObjectiveField(phaseIdx, objIdx, 'type', e.target.value)
                          // Clear type-incompatible fields so CHECK doesn't fire.
                          setObjectiveField(phaseIdx, objIdx, 'target_id', '')
                          setObjectiveField(phaseIdx, objIdx, 'destination_id', '')
                          setObjectiveField(phaseIdx, objIdx, 'duration_minutes', '')
                        }} style={C.select}>
                          {OBJECTIVE_TYPES.map(t => <option key={t.value} value={t.value} style={{ background: '#0a140a' }}>{t.label}</option>)}
                        </select>
                      </div>
                      {def.needs && (
                        <div>
                          <label style={C.label}>{def.needs.toUpperCase()} TARGET *</label>
                          <select value={obj.target_id} onChange={e => setObjectiveField(phaseIdx, objIdx, 'target_id', e.target.value)} style={C.select}>
                            <option value="">— pick —</option>
                            {renderTargetOptions()}
                          </select>
                        </div>
                      )}
                      {def.duration && (
                        <div>
                          <label style={C.label}>DURATION (min) *</label>
                          <input type="number" value={obj.duration_minutes}
                            onChange={e => setObjectiveField(phaseIdx, objIdx, 'duration_minutes', e.target.value)}
                            min={1} max={1440} style={C.input} />
                        </div>
                      )}
                      {def.destination && (
                        <div>
                          <label style={C.label}>DESTINATION CHECKPOINT *</label>
                          <select value={obj.destination_id} onChange={e => setObjectiveField(phaseIdx, objIdx, 'destination_id', e.target.value)} style={C.select}>
                            <option value="">— pick a checkpoint —</option>
                            {checkpoints.map(cp => <option key={cp.id} value={cp.id} style={{ background: '#0a140a' }}>{cp.name}</option>)}
                          </select>
                        </div>
                      )}
                      <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
                        <input type="checkbox" id={`concurrent-${phaseIdx}-${objIdx}`}
                          checked={obj.concurrent} onChange={e => setObjectiveField(phaseIdx, objIdx, 'concurrent', e.target.checked)} />
                        <label htmlFor={`concurrent-${phaseIdx}-${objIdx}`} style={{ ...C.mono, fontSize: 10, color: '#4aaa5a' }}>
                          Concurrent — runs alongside other objectives in this phase
                        </label>
                      </div>

                      {/* ANU-168 — fail condition toggle. Only relevant
                          for entity-scan types (not manual / timed). */}
                      {!['manual','timed_hold'].includes(obj.type) && (
                        <>
                          <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
                            <input type="checkbox" id={`failcond-${phaseIdx}-${objIdx}`}
                              checked={obj.is_fail_condition} onChange={e => setObjectiveField(phaseIdx, objIdx, 'is_fail_condition', e.target.checked)} />
                            <label htmlFor={`failcond-${phaseIdx}-${objIdx}`} style={{ ...C.mono, fontSize: 10, color: '#ff4d4d' }}>
                              ⚠ Fail condition — enemy scan of this target auto-fails the FRAGO
                            </label>
                          </div>
                          {obj.is_fail_condition && (
                            <div>
                              <label style={{ ...C.label, color: '#ff4d4d' }}>FAIL SCOPE</label>
                              <select
                                value={obj.fail_faction_scope || 'opposing'}
                                onChange={e => setObjectiveField(phaseIdx, objIdx, 'fail_faction_scope', e.target.value)}
                                style={{ ...C.select, borderColor: 'rgba(255,77,77,.4)', color: '#ff4d4d' }}
                              >
                                <option value="opposing" style={{ background: '#0a140a' }}>Opposing faction only</option>
                                <option value="any" style={{ background: '#0a140a' }}>Any faction</option>
                              </select>
                            </div>
                          )}
                        </>
                      )}

                      {/* FRAGO restructure — per-objective notify-on-
                          complete. When the objective lands, push the
                          message to commander / faction / all per the
                          who selector. */}
                      <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
                        <input type="checkbox" id={`notify-${phaseIdx}-${objIdx}`}
                          checked={obj.notify_enabled} onChange={e => setObjectiveField(phaseIdx, objIdx, 'notify_enabled', e.target.checked)} />
                        <label htmlFor={`notify-${phaseIdx}-${objIdx}`} style={{ ...C.mono, fontSize: 10, color: '#9aff9a' }}>
                          Notify when this objective completes
                        </label>
                      </div>
                      {obj.notify_enabled && (
                        <>
                          <div>
                            <label style={C.label}>NOTIFY WHO</label>
                            <select value={obj.notify_who} onChange={e => setObjectiveField(phaseIdx, objIdx, 'notify_who', e.target.value)} style={C.select}>
                              <option value="commander" style={{ background: '#0a140a' }}>Commander only</option>
                              <option value="faction" style={{ background: '#0a140a' }}>Whole faction</option>
                              <option value="all" style={{ background: '#0a140a' }}>Everyone in event</option>
                            </select>
                          </div>
                          <div>
                            <label style={C.label}>NOTIFY MESSAGE</label>
                            <input value={obj.notify_message} onChange={e => setObjectiveField(phaseIdx, objIdx, 'notify_message', e.target.value)}
                              placeholder="Pilot located, awaiting extraction" maxLength={200} style={C.input} />
                          </div>
                        </>
                      )}

                      {/* FRAGO restructure — per-objective opposing-team
                          effect. When the objective lands, fire an alert
                          to the OPPOSING faction(s). Optionally chain a
                          counter-FRAGO for them. */}
                      <div style={{ gridColumn: '1 / -1' }} className="flex items-center gap-2">
                        <input type="checkbox" id={`oppose-${phaseIdx}-${objIdx}`}
                          checked={obj.opposing_enabled} onChange={e => setObjectiveField(phaseIdx, objIdx, 'opposing_enabled', e.target.checked)} />
                        <label htmlFor={`oppose-${phaseIdx}-${objIdx}`} style={{ ...C.mono, fontSize: 10, color: '#ff7a3d' }}>
                          Opposing team effect on completion
                        </label>
                      </div>
                      {obj.opposing_enabled && (
                        <>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.label, color: '#ff7a3d' }}>OPPOSING MESSAGE</label>
                            <input value={obj.opposing_message} onChange={e => setObjectiveField(phaseIdx, objIdx, 'opposing_message', e.target.value)}
                              placeholder="Movement detected in sector 4 — investigate" maxLength={200}
                              style={{ ...C.input, borderColor: 'rgba(255,122,61,.4)', color: '#ff7a3d' }} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.label, color: '#ff7a3d' }}>CHAIN COUNTER-FRAGO (optional)</label>
                            <select value={obj.opposing_trigger_frago_id} onChange={e => setObjectiveField(phaseIdx, objIdx, 'opposing_trigger_frago_id', e.target.value)}
                              style={{ ...C.select, borderColor: 'rgba(255,122,61,.4)', color: '#ff7a3d' }}>
                              <option value="" style={{ background: '#0a140a' }}>— none —</option>
                              {queuedFragos.map(f => (
                                <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>{f.mission_name || f.id.slice(0, 8)}</option>
                              ))}
                            </select>
                          </div>
                          {/* FRAGO feature — activate a queued non-FRAGO
                              bounty on the opposing side when this
                              objective lands. e.g. "Kill the pilot" or
                              "Defend the pilot" can drop on either
                              faction when the previous objective
                              resolves. */}
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ ...C.label, color: '#ff7a3d' }}>ACTIVATE BOUNTY (optional)</label>
                            <select value={obj.opposing_activate_bounty_id} onChange={e => setObjectiveField(phaseIdx, objIdx, 'opposing_activate_bounty_id', e.target.value)}
                              style={{ ...C.select, borderColor: 'rgba(255,122,61,.4)', color: '#ff7a3d' }}>
                              <option value="" style={{ background: '#0a140a' }}>— none —</option>
                              {queuedBounties.map(b => (
                                <option key={b.id} value={b.id} style={{ background: '#0a140a' }}>
                                  {bountyOptionLabel(b)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
              <button onClick={() => addObjective(phaseIdx)} style={C.btnGhost}>
                <Plus size={11} /> ADD OBJECTIVE
              </button>
              {/* ANU-102 — phase-level linked bounties */}
              {linkedBounties.some((lb, _i) => lb.activation_mode === 'on_phase' && parseInt(lb.activation_phase_idx, 10) === phaseIdx) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {linkedBounties.map((lb, gIdx) => {
                    if (lb.activation_mode !== 'on_phase' || parseInt(lb.activation_phase_idx, 10) !== phaseIdx) return null
                    const tp = players.find(p => p.user_id === lb.target_id)
                    return (
                      <div key={gIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,.2)', border: '1px dashed rgba(255,140,40,.3)', padding: '4px 8px' }}>
                        <div>
                          <p style={{ ...C.mono, fontSize: 10, color: '#ff9a3d' }}>{lb.title?.trim() || `${lb.type.toUpperCase()} BOUNTY`} · {lb.points} pts</p>
                          <p style={{ ...C.mono, fontSize: 9, color: '#8a6a4a' }}>{lb.type.toUpperCase()} · {tp?.users?.callsign || tp?.users?.display_name || '—'} · on phase complete</p>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" onClick={() => openLinkedDraftForEdit(gIdx)} style={{ background: 'none', border: '1px solid rgba(255,140,40,.3)', color: '#ff9a3d', cursor: 'pointer', padding: '3px 6px', fontSize: 10, minHeight: 26 }}><Edit2 size={9} /></button>
                          <button type="button" onClick={() => removeLinkedBounty(gIdx)} style={{ background: 'none', border: '1px solid rgba(255,42,42,.3)', color: '#ff6a6a', cursor: 'pointer', padding: '3px 6px', fontSize: 10, minHeight: 26 }}><Trash2 size={9} /></button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {lbDraft?.activation_mode === 'on_phase' && parseInt(lbDraft?.activation_phase_idx, 10) === phaseIdx
                ? renderLbDraftForm()
                : !lbDraft
                  ? <button type="button" onClick={() => openLinkedDraftForPhase(phaseIdx)} style={{ ...C.btnGhost, borderColor: 'rgba(255,140,40,.25)', color: '#cc7a2a', fontSize: 10, marginTop: 6 }}>
                      <Plus size={10} /> ADD BOUNTY (phase)
                    </button>
                  : null
              }
            </div>
          ))}
          <button onClick={addPhase} style={C.btnGhost}>
            <Plus size={12} /> ADD PHASE
          </button>
        </>)}
      </div>

      {/* ───────────── SECTION 4: END CONDITIONS ───────────── */}
      <div className="p-3 mb-3" style={C.panelGreen}>
        <button onClick={() => setStep3Collapsed(s => !s)} className="flex items-center gap-2 w-full text-left"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9aff9a', padding: 0, marginBottom: step3Collapsed ? 0 : 12 }}>
          {step3Collapsed ? <ChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronUp size={14} />}
          <span style={{ ...C.stepHeader, color: '#9aff9a' }}>◆ END CONDITIONS</span>
        </button>
        {!step3Collapsed && (() => {
          // FRAGO restructure — auto-tally view. The total a player
          // sees is the sum of phaseScore across phases, unless the GM
          // pins an override. Empty / non-numeric phase scores count
          // as zero so the tally still makes sense as the GM types.
          const phaseScoreSum = phases.reduce((sum, p) => {
            const n = parseInt(p.phaseScore, 10)
            return sum + (Number.isFinite(n) ? n : 0)
          }, 0)
          const tallyParts = phases
            .map((p, i) => `Phase ${i + 1} (${parseInt(p.phaseScore, 10) || 0})`)
            .join(' + ')
          return (
            <>
              <div className="p-2 mb-3" style={{ background: 'rgba(0,20,8,.3)', border: '1px solid rgba(154,255,154,.3)' }}>
                <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}>
                  Auto-tally: {tallyParts || 'Phase 1 (0)'} = <strong>{phaseScoreSum} pts</strong>
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a' }}>OVERRIDE TOTAL</label>
                  <input type="number" value={endScoreOverride} onChange={e => setEndScoreOverride(e.target.value)}
                    placeholder={`(auto: ${phaseScoreSum})`} min={0} max={100000}
                    style={{ ...C.input, width: 140 }} />
                  <span style={{ ...C.mono, fontSize: 10, color: '#2a6a3a' }}>
                    {endScoreOverride === '' ? 'Auto-tally in effect' : `Pinned at ${endScoreOverride} pts`}
                  </span>
                </div>
              </div>
              <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', marginBottom: 8 }}>
                Each outcome is optional — leave blank to do nothing. Timeout is treated as a fail outcome.
              </p>
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <ResolutionColumn label="ON SUCCESS" color="#9aff9a" data={onSuccess} setData={setOnSuccess}
                  queuedBounties={queuedBounties} queuedFragos={queuedFragos} />
                <ResolutionColumn label="ON FAIL" color="#ff7a3d" data={onFail} setData={setOnFail}
                  queuedBounties={queuedBounties} queuedFragos={queuedFragos} />
              </div>
            </>
          )
        })()}
      </div>

      {/* ───────────── ANU-82: LINKED BOUNTIES ─────────────
          Sits inside End Conditions, above the submit row. Each entry
          here writes a child bounty row at FRAGO save with parent_frago_id
          set + status='queued' + linked_trigger metadata. Cascade triggers
          on bounties / frago_phases / frago_objectives activate or cancel
          these children at the right moment. */}
      <div className="p-3 mb-3" style={{ ...C.panel, border: '1px solid rgba(255,140,40,.3)', background: 'rgba(255,140,40,.04)' }}>
        <div className="flex items-center justify-between mb-2">
          <span style={{ ...C.stepHeader, color: '#ff9a3d', fontSize: 14 }}><Link2 size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />LINKED BOUNTIES</span>
          {!lbDraft && (
            <button onClick={openLinkedDraftForNew} style={{ ...C.btnGhost, borderColor: 'rgba(255,140,40,.35)', color: '#ff9a3d' }}>
              <Plus size={12} /> ADD BOUNTY TO FRAGO
            </button>
          )}
        </div>

        {linkedBounties.length === 0 && !lbDraft && (
          <p style={{ ...C.mono, fontSize: 10, color: '#8a6a4a' }}>
            Optional. Add bounties that activate / cancel when this FRAGO hits its triggers.
          </p>
        )}

        {/* Saved linked bounty cards — frago-level only (phase bounties shown under their phase card) */}
        {linkedBounties.some(lb => lb.activation_mode !== 'on_phase') && !lbDraft && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            {linkedBounties.map((lb, idx) => {
              if (lb.activation_mode === 'on_phase') return null
              const targetPlayer = players.find(p => p.user_id === lb.target_id)
              const targetLabel = targetPlayer?.users?.callsign || targetPlayer?.users?.display_name || '—'
              const titleStr = lb.title?.trim() || `${lb.type.toUpperCase()} BOUNTY`
              const trig = lb.activation_mode === 'with_frago'
                ? 'with FRAGO'
                : lb.activation_mode === 'on_phase'
                  ? `on Phase ${parseInt(lb.activation_phase_idx, 10) + 1}`
                  : `on objective P${parseInt(lb.activation_objective_phase_idx, 10) + 1}/O${parseInt(lb.activation_objective_idx, 10) + 1}`
              const cancelStr = lb.cancel_mode === 'never' ? '' : ` · cancel ${lb.cancel_mode.replace(/_/g, ' ')}`
              return (
                <div key={idx} style={{ background: 'rgba(0,0,0,.3)', border: '1px solid rgba(255,140,40,.25)', padding: '8px 10px', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {lb.type === 'kill' ? <TargetIcon size={12} style={{ color: '#ff6a3d' }} /> : <ShieldIcon size={12} style={{ color: '#4dd6c2' }} />}
                      <span style={{ ...C.mono, fontSize: 12, color: '#ff9a3d' }}>{titleStr}</span>
                      <span style={{ ...C.mono, fontSize: 10, color: '#8a6a4a' }}>→ {targetLabel}</span>
                      <span style={{ ...C.mono, fontSize: 10, color: '#ffcc33' }}>{lb.points} pts</span>
                    </div>
                    <p style={{ ...C.mono, fontSize: 10, color: '#8a6a4a', marginTop: 2 }}>
                      {lb.type.toUpperCase()} · {lb.active_duration} {lb.active_duration_unit} · activate {trig}{cancelStr}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => openLinkedDraftForEdit(idx)} style={{ background: 'none', border: '1px solid rgba(255,140,40,.3)', color: '#ff9a3d', cursor: 'pointer', padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, minHeight: 30 }}>
                      <Edit2 size={10} />
                    </button>
                    <button type="button" onClick={() => removeLinkedBounty(idx)} style={{ background: 'none', border: '1px solid rgba(255,42,42,.3)', color: '#ff6a6a', cursor: 'pointer', padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, minHeight: 30 }}>
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Inline draft form — phase bounties render inline within their phase card above */}
        {lbDraft && lbDraft.activation_mode !== 'on_phase' && renderLbDraftForm()}
      </div>

      <div className="flex gap-2 mt-4">
        <button onClick={handleSubmit} disabled={submitting} style={{ ...C.btnPrimary, opacity: submitting ? 0.5 : 1 }}>
          <Check size={14} /> {submitting ? 'SAVING...' : isEditMode ? 'SAVE CHANGES' : 'CREATE FRAGO'}
        </button>
        <button onClick={onCancel} style={C.btnSecondary}>CANCEL</button>
      </div>
    </div>
  )
}

// ANU-85 — readable bounty option label. Format: "Mission Name
// (TargetCallsign) — Type · X pts", or "Bounty #abcd — Type —
// TargetCallsign · X pts" when no mission name. Trims the gaps so we
// don't print "(undefined)" or trailing em-dashes for thin rows.
function bountyOptionLabel(b) {
  const id4 = b.id ? b.id.slice(0, 4) : '????'
  const title = b.mission_name?.trim() || `Bounty #${id4}`
  const type = (b.bounty_type || 'bounty').replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
  const target = b.target?.callsign || b.target?.display_name || null
  const pts = (b.points != null) ? ` · ${b.points} pts` : ''
  let main
  if (b.mission_name?.trim()) {
    main = target ? `${title} (${target}) — ${type}` : `${title} — ${type}`
  } else {
    main = target ? `${title} — ${type} — ${target}` : `${title} — ${type}`
  }
  return main + pts
}

function ResolutionColumn({ label, color, data, setData, queuedBounties, queuedFragos }) {
  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }))
  // FRAGO restructure — points input removed. Phase scores + the
  // end_score_override own the point math now; resolutions only do
  // side effects (activate a queued bounty, chain a FRAGO, release a
  // narrative message).
  return (
    <div className="p-2" style={{ background: 'rgba(0,20,8,.3)', border: `1px solid ${color}33` }}>
      <p style={{ ...C.mono, fontSize: 10, color, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</p>
      <div className="space-y-2">
        <div>
          <label style={C.label}>ACTIVATE BOUNTY</label>
          <select value={data.activate_bounty_id} onChange={e => set('activate_bounty_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedBounties.map(b => (
              <option key={b.id} value={b.id} style={{ background: '#0a140a' }}>
                {bountyOptionLabel(b)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={C.label}>CHAIN NEXT FRAGO</label>
          <select value={data.trigger_frago_id} onChange={e => set('trigger_frago_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedFragos.map(f => (
              <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>
                {f.mission_name || f.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={C.label}>RELEASE MESSAGE</label>
          <textarea value={data.message} onChange={e => set('message', e.target.value)} rows={2} maxLength={400}
            placeholder="Intel: enemy convoy spotted at grid 14..."
            style={{ ...C.input, resize: 'vertical', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
        </div>
        {/* SEC 5 — cancel / fail cascades. Pickers stay single-id;
            cleanResolution wraps each into the plural array shape
            the server trigger reads. */}
        <div>
          <label style={C.label}>CANCEL BOUNTY</label>
          <select value={data.cancel_bounty_id} onChange={e => set('cancel_bounty_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedBounties.map(b => (
              <option key={b.id} value={b.id} style={{ background: '#0a140a' }}>
                {bountyOptionLabel(b)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={C.label}>CANCEL FRAGO</label>
          <select value={data.cancel_frago_id} onChange={e => set('cancel_frago_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedFragos.map(f => (
              <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>
                {f.mission_name || f.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={C.label}>FAIL BOUNTY</label>
          <select value={data.fail_bounty_id} onChange={e => set('fail_bounty_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedBounties.map(b => (
              <option key={b.id} value={b.id} style={{ background: '#0a140a' }}>
                {bountyOptionLabel(b)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={C.label}>FAIL FRAGO</label>
          <select value={data.fail_frago_id} onChange={e => set('fail_frago_id', e.target.value)} style={C.select}>
            <option value="">— none —</option>
            {queuedFragos.map(f => (
              <option key={f.id} value={f.id} style={{ background: '#0a140a' }}>
                {f.mission_name || f.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
